import Decorator from './decorator'
import { LocationDecorator, LoadDecorator, InvoiceDecorator } from 'decorators'
import { isObject, join, padStart, includes, map } from 'lodash'
import accounting from 'accounting'

export default class JobDecorator extends Decorator {
  /**
   * Build the commodity and location string.
   *
   * @return string
   */
  commodityAndLocation () {
    const hasCommodity = isObject(this.commodity)
    const hasLocationFrom = isObject(this.location_from)
    const hasLocationTo = isObject(this.location_to)

    let response = []

    if (hasCommodity) {
      response.push(this.commodity.name)
    }

    if (hasLocationFrom) {
      response.push(hasCommodity ? 'from' : 'From')
      response.push(this.location_from.formattedName)
    }

    if (hasLocationTo) {
      response.push(hasCommodity || hasLocationFrom ? 'to' : 'To')
      response.push(this.location_to.formattedName)
    }

    return join(response, ' ')
  }

  commodityAndLocationWithState () {
    const hasCommodity = isObject(this.commodity)
    const hasLocationFrom = isObject(this.location_from)
    const hasLocationTo = isObject(this.location_to)

    let response = []

    if (hasCommodity) {
      response.push(this.commodity.name)
    }

    if (hasLocationFrom) {
      response.push(hasCommodity ? 'from' : 'From')
      response.push(this.location_from.nameWithShortState)
    }

    if (hasLocationTo) {
      response.push(hasCommodity || hasLocationFrom ? 'to' : 'To')
      response.push(this.location_to.nameWithShortState)
    }

    return join(response, ' ')
  }

  commodityAndLocationWithQuantity () {
    if (this.decorated.quantity) {
      return `${this.decorated.quantity}t of ${this.commodityAndLocationWithState}`
    }

    return this.commodityAndLocation
  }

  commodityAndLocationWithPublicQuantity () {
    if (this.decorated.public_quantity) {
      return `${this.decorated.public_quantity}t of ${this.commodityAndLocationWithState}`
    }

    return this.commodityAndLocation
  }

  /**
   * Number is the ID padded with 5 zeroes and prefixed with AJ.
   *
   * @return string
   */
  number () {
    return `AL${padStart(this.id, 5, '0')}`
  }

  /**
   * The status text corrosponding to each status.
   *
   * @return string
   */
  statusText () {
    if (this.status === 'pending') {
      return 'Pending'
    } else if (this.status === 'accepted') {
      return 'Accepted'
    } else if (this.status === 'in_progress') {
      return 'In Progress'
    } else if (this.status === 'finalising') {
      return 'Finalising'
    } else if (this.status === 'completed') {
      return 'Completed'
    } else if (this.status === 'declined') {
      return 'Request Cancelled'
    } else if (this.status === 'cancelled') {
      return 'Cancelled'
    } else if (this.status === 'invoiced') {
      return 'Invoiced'
    }
  }

  /**
   * Get the rate formatted as money.
   *
   * @return string
   */
  rate () {
    if (this.decorated.rate) {
      return `${accounting.formatMoney(this.decorated.rate)}/t`
    }

    return '–'
  }

  /**
   * The class to apply based on the status.
   *
   * @return string
   */
  statusClass () {
    if (includes(['pending', 'accepted', 'in_progress', 'finalising', 'completed', 'invoiced'], this.status)) {
      return 'primary'
    } else if (this.status === 'declined' || this.status === 'cancelled') {
      return 'danger'
    }
  }

  /**
   * The progress completed percentage of the job.
   *
   * This should not be used for a reactive update as it is based on the tonnage completed
   * returned from the API.
   *
   * @return integer
   */
  progressCompletedPercentage () {
    if (this.decorated.status === 'declined' || this.decorated.status === 'pending') {
      return 0
    } else if (this.decorated.status === 'completed') {
      return 100
    }

    let completed = (this.decorated.tonnage_completed / this.decorated.quantity) * 100

    if (completed > 100) {
      completed = 100
    }

    return accounting.formatNumber(completed, 2)
  }

  /**
   * Tonnage completed in tons.
   *
   * @return string
   */
  tonnageCompleted () {
    if (this.decorated.tonnage_completed) {
      return `${this.decorated.tonnage_completed}t`
    }

    return '0t'
  }

  startAt () {
    if (this.decorated.start_at) {
      return this.decorated.start_at
    }

    return '–'
  }

  endAt () {
    if (this.decorated.end_at) {
      return this.decorated.end_at
    }

    return '–'
  }

  updatedAt () {
    if (this.decorated.updated_at) {
      return this.decorated.updated_at
    }

    return '–'
  }

  notes () {
    if (this.decorated.notes) {
      return this.decorated.notes
    }

    return '–'
  }

  quantity () {
    if (this.decorated.quantity) {
      return `${this.decorated.quantity}t`
    }

    return '–'
  }

  /**
   * Address details, e.g. (street name, number, suburb, state)
   *
   * @return string
   */
  locationFrom () {
    const hasLocationFrom = isObject(this.decorated.location_from)

    if (hasLocationFrom) {
      return new LocationDecorator(this.decorated.location_from)
    }

    return null
  }

  locationTo () {
    const hasLocationTo = isObject(this.decorated.location_to)

    if (hasLocationTo) {
      return new LocationDecorator(this.decorated.location_to)
    }

    return null
  }

  orderReference () {
    if (this.decorated.order_reference) {
      return `${this.decorated.order_reference}`
    }

    return '–'
  }

  pickupReference () {
    if (this.decorated.pickup_reference) {
      return `${this.decorated.pickup_reference}`
    }

    return '–'
  }

  deliveryReference () {
    if (this.decorated.delivery_reference) {
      return `${this.decorated.delivery_reference}`
    }

    return '–'
  }

  loadsDecorated () {
    return map(this.decorated.loads, load => (new LoadDecorator(load)))
  }

  invoice () {
    if (this.decorated.invoice) {
      return new InvoiceDecorator(this.decorated.invoice)
    }
  }
}



// WEBPACK FOOTER //
// ./src/decorators/job.js